import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Trophy,
  Users,
  Dice5,
  RefreshCw,
  Loader2,
  Swords,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * NinjaDrawDialog — fetches top scorers for a ninja game and lets
 * admin/merchant pick the top N as winners. Ranking tiebreak: faster time
 * wins in case of equal score.
 */
export default function NinjaDrawDialog({ open, onOpenChange, game }) {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [winnersCount, setWinnersCount] = useState(game?.winners_count || 1);
  const [drawing, setDrawing] = useState(false);
  const [winners, setWinners] = useState([]);

  useEffect(() => {
    if (!open || !game?.id) return;
    setWinners([]);
    setStats(null);
    api
      .get(`/games/${game.id}/ninja/stats`)
      .then((r) => {
        setStats(r.data);
        if (r.data.winners?.length) setWinners(r.data.winners);
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, [open, game?.id]);

  async function runDraw() {
    if (drawing) return;
    setDrawing(true);
    try {
      const { data } = await api.post(`/games/${game.id}/ninja/draw`, {
        winners_count: winnersCount,
      });
      await new Promise((r) => setTimeout(r, 700));
      setWinners(data.winners || []);
      const s = await api.get(`/games/${game.id}/ninja/stats`);
      setStats(s.data);
      if (data.winners?.length) {
        toast.success(t("lottery.draw_success"));
      } else {
        toast.info(t("dashboard.no_data"));
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDrawing(false);
    }
  }

  const hasPrev = !!stats?.lottery_drawn_at;
  const alreadyDrew = hasPrev && winners.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="ninja-draw-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" style={{ color: "#d4af37" }} />
            {t("game_editor.ninja_draw_btn")}
          </DialogTitle>
          <DialogDescription>{game?.title}</DialogDescription>
        </DialogHeader>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 py-2">
          <div className="qrise-card p-3">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground inline-flex items-center gap-1.5">
              <Users className="h-3 w-3" />
              {t("lottery.total_participants")}
            </div>
            <div
              className="mt-1 text-2xl font-semibold tracking-tight"
              data-testid="ninja-total-participants"
            >
              {stats?.total_participants ?? "—"}
            </div>
          </div>
          <div className="qrise-card p-3">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground inline-flex items-center gap-1.5">
              <Swords className="h-3 w-3" />
              {t("game_editor.ninja_best_score")}
            </div>
            <div className="mt-1 text-2xl font-semibold tracking-tight text-emerald-600 dark:text-emerald-400">
              {stats?.top?.[0]?.score ?? "—"}
            </div>
          </div>
          <div className="qrise-card p-3">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
              {t("game_editor.ninja_winners_drawn")}
            </div>
            <div className="mt-1 text-2xl font-semibold tracking-tight">
              {stats?.winners_count ?? "—"}
            </div>
          </div>
        </div>

        {/* Winners count input + draw button */}
        <div className="rounded-xl border border-border p-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[140px]">
            <Label className="text-xs font-medium">
              {t("lottery.winners_to_draw")}
            </Label>
            <Input
              type="number"
              min={1}
              max={stats?.total_participants || 100}
              value={winnersCount}
              onChange={(e) =>
                setWinnersCount(Math.max(1, parseInt(e.target.value) || 1))
              }
              className="mt-1.5 rounded-xl h-10"
              data-testid="ninja-winners-input"
            />
          </div>
          <Button
            type="button"
            onClick={runDraw}
            disabled={drawing || !stats?.total_participants}
            className="h-10 rounded-xl text-xs font-bold tracking-[0.2em] uppercase"
            style={{
              background:
                "linear-gradient(180deg, #f5e4bf 0%, #d4af37 55%, #a98027 100%)",
              color: "#1a1410",
              boxShadow:
                "0 10px 30px -8px rgba(212,175,55,0.55), inset 0 1px 0 rgba(255,255,255,0.35)",
            }}
            data-testid="ninja-run-draw-btn"
          >
            {drawing ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                {t("lottery.drawing")}
              </>
            ) : alreadyDrew ? (
              <>
                <RefreshCw className="h-4 w-4 mr-1.5" />
                {t("lottery.redraw")}
              </>
            ) : (
              <>
                <Dice5 className="h-4 w-4 mr-1.5" />
                {t("lottery.draw_now")}
              </>
            )}
          </Button>
        </div>

        {/* Winners */}
        {winners.length > 0 && (
          <div
            className="rounded-xl border border-amber-400/40 bg-amber-400/10 p-4"
            data-testid="ninja-winners-list"
          >
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-amber-700 dark:text-amber-300 inline-flex items-center gap-1.5 mb-3">
              <Trophy className="h-3.5 w-3.5" />
              {t("lottery.winners_title", { count: winners.length })}
            </div>
            <ul className="space-y-2">
              {winners.map((w, i) => (
                <li
                  key={w.session_id}
                  className="flex items-center gap-3 rounded-lg bg-card border border-border px-3 py-2"
                  data-testid={`ninja-winner-${i}`}
                >
                  <div
                    className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{
                      background:
                        i === 0
                          ? "linear-gradient(180deg,#f5e4bf,#d4af37,#a98027)"
                          : i === 1
                            ? "linear-gradient(180deg,#e5e7eb,#9ca3af,#6b7280)"
                            : i === 2
                              ? "linear-gradient(180deg,#fcd7b6,#d08a4e,#9a5a2a)"
                              : "linear-gradient(180deg,#cbd5e1,#64748b)",
                      color: "#1a1410",
                    }}
                  >
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">
                      {w.first_name} {w.last_name}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono truncate">
                      {w.email} · {w.phone}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-bold tabular-nums">
                      {w.score}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {(w.time_taken_ms / 1000).toFixed(1)}s
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {hasPrev && !drawing && (
          <div className="text-[11px] text-muted-foreground text-center">
            {t("lottery.drawn_at", {
              date: new Date(stats.lottery_drawn_at).toLocaleString(),
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
